import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type {
    GridFilterModel,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid-pro'

/**
 * Default page size used when none is provided. Mirrors the Data Grid default
 * configured in the shared `Table` component.
 */
const DEFAULT_PAGE_SIZE = 100

export type GridUrlStateOptions = {
    /** Default pagination applied when the URL has no `page`/`pageSize`. */
    defaultPaginationModel?: GridPaginationModel
    /** Default sort applied when the URL has no `sort`. */
    defaultSortModel?: GridSortModel
    /** Default filter applied when the URL has neither `filter` nor `q`. */
    defaultFilterModel?: GridFilterModel
    /**
     * When true (default), changing the filter resets pagination to page 0.
     * Recommended to avoid landing on a page that no longer exists after
     * a filter narrows the result set.
     */
    resetPageOnFilterChange?: boolean
    /** When true, changing the sort resets pagination to page 0. */
    resetPageOnSortChange?: boolean
    /**
     * Extra arbitrary URL params consumed by the caller (e.g. external
     * filters). They are preserved by helpers like `resetExtra`.
     */
    extraParamKeys?: string[]
}

const serializeSort = (model: GridSortModel) =>
    model
        .map((it) => `${it.field}:${it.sort === 'desc' ? 'desc' : 'asc'}`)
        .join(',')

const parseSortParam = (raw: string | null, fallback: GridSortModel): GridSortModel => {
    if (!raw) return fallback
    const parsed = raw
        .split(',')
        .map((item) => {
            const [field, direction] = item.split(':')
            if (!field) return null
            return {
                field: field.trim(),
                sort: direction === 'desc' ? ('desc' as const) : ('asc' as const),
            }
        })
        .filter((item): item is { field: string; sort: 'asc' | 'desc' } => Boolean(item))
    return parsed.length ? parsed : fallback
}

const serializeFilter = (model: GridFilterModel): string => {
    const items = (model.items || []).filter((it) => it.field && it.operator)
    return JSON.stringify({
        // `id` is preserved across the URL round-trip so the Data Grid keeps
        // identifying each filter row across re-renders. Without it, MUI X
        // assigns new ids on every parse and ends up dropping any
        // in-progress filter row the user just added via "Add Filter".
        items: items.map((it) => ({
            ...(it.id !== undefined ? { id: it.id } : {}),
            field: it.field,
            operator: it.operator,
            ...(it.value !== undefined ? { value: it.value } : {}),
        })),
        ...(model.logicOperator ? { logicOperator: model.logicOperator } : {}),
    })
}

const parseFilterParam = (
    rawFilter: string | null,
    rawQuick: string | null,
    fallback: GridFilterModel,
): GridFilterModel => {
    if (!rawFilter && !rawQuick) return fallback
    const model: GridFilterModel = { items: [] }
    if (rawFilter) {
        try {
            const parsed = JSON.parse(rawFilter)
            if (Array.isArray(parsed?.items)) {
                model.items = parsed.items
                    .filter(
                        (it: any) =>
                            it && typeof it.field === 'string' && typeof it.operator === 'string',
                    )
                    .map((it: any) => ({
                        ...(it.id !== undefined ? { id: it.id } : {}),
                        field: it.field,
                        operator: it.operator,
                        ...(it.value !== undefined ? { value: it.value } : {}),
                    }))
            }
            if (parsed?.logicOperator === 'and' || parsed?.logicOperator === 'or') {
                model.logicOperator = parsed.logicOperator
            }
        } catch {
            // Ignore malformed payloads, fall back to empty filter.
        }
    }
    if (rawQuick) {
        const quickValues = rawQuick.split(/\s+/).filter(Boolean)
        if (quickValues.length) model.quickFilterValues = quickValues
    }
    return model
}

/**
 * Two-way sync between MUI X Data Grid state and URL search params.
 *
 * The hook keeps `paginationModel`, `sortModel` and `filterModel` stored in the
 * URL so the listing can be shared/bookmarked. It also exposes
 * `apiQueryParams` already serialized for the backend (which uses the same
 * grammar via {@link parseGridQueryParams} in `packages/api`).
 */
export const useGridUrlState = (options: GridUrlStateOptions = {}) => {
    const {
        defaultPaginationModel = { page: 0, pageSize: DEFAULT_PAGE_SIZE },
        defaultSortModel = [],
        defaultFilterModel = { items: [] },
        resetPageOnFilterChange = true,
        resetPageOnSortChange = false,
    } = options

    const [searchParams, setSearchParams] = useSearchParams()

    const paginationModel = useMemo<GridPaginationModel>(() => {
        const page = parseInt(searchParams.get('page') ?? '', 10)
        const pageSize = parseInt(searchParams.get('pageSize') ?? '', 10)
        return {
            page: Number.isFinite(page) && page > 0 ? page : defaultPaginationModel.page,
            pageSize:
                Number.isFinite(pageSize) && pageSize > 0
                    ? pageSize
                    : defaultPaginationModel.pageSize,
        }
    }, [searchParams, defaultPaginationModel.page, defaultPaginationModel.pageSize])

    const sortModel = useMemo<GridSortModel>(
        () => parseSortParam(searchParams.get('sort'), defaultSortModel),
        [searchParams, defaultSortModel],
    )

    const filterModel = useMemo<GridFilterModel>(
        () =>
            parseFilterParam(
                searchParams.get('filter'),
                searchParams.get('q'),
                defaultFilterModel,
            ),
        [searchParams, defaultFilterModel],
    )

    const updateParams = useCallback(
        (mutator: (next: URLSearchParams) => void) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev)
                    mutator(next)
                    return next
                },
                { replace: true },
            )
        },
        [setSearchParams],
    )

    const setPaginationModel = useCallback(
        (model: GridPaginationModel) => {
            updateParams((next) => {
                if (model.page === defaultPaginationModel.page) next.delete('page')
                else next.set('page', String(model.page))

                if (model.pageSize === defaultPaginationModel.pageSize)
                    next.delete('pageSize')
                else next.set('pageSize', String(model.pageSize))
            })
        },
        [updateParams, defaultPaginationModel.page, defaultPaginationModel.pageSize],
    )

    const setSortModel = useCallback(
        (model: GridSortModel) => {
            updateParams((next) => {
                // Always persist sort to URL, even when empty, so that clearing
                // the sort isn't overwritten by the default on the next render.
                next.set('sort', serializeSort(model))
                if (resetPageOnSortChange) next.delete('page')
            })
        },
        [updateParams, resetPageOnSortChange],
    )

    const setFilterModel = useCallback(
        (model: GridFilterModel) => {
            updateParams((next) => {
                // Always persist filter to URL (even when empty) so that the
                // user clearing all filters is respected on re-renders.
                next.set('filter', serializeFilter(model))

                const quick = (model.quickFilterValues || []).filter(Boolean).join(' ')
                if (!quick) next.delete('q')
                else next.set('q', quick)

                if (resetPageOnFilterChange) next.delete('page')
            })
        },
        [updateParams, resetPageOnFilterChange],
    )

    /** Serialized params that the backend understands. */
    const apiQueryParams = useMemo<Record<string, string>>(() => {
        const params: Record<string, string> = {
            page: String(paginationModel.page),
            pageSize: String(paginationModel.pageSize),
        }
        if (sortModel.length) params.sort = serializeSort(sortModel)
        const items = (filterModel.items || []).filter((it) => it.field && it.operator)
        if (items.length || filterModel.logicOperator) {
            params.filter = serializeFilter(filterModel)
        }
        const quick = (filterModel.quickFilterValues || []).filter(Boolean).join(' ')
        if (quick) params.q = quick
        return params
    }, [paginationModel, sortModel, filterModel])

    return {
        paginationModel,
        sortModel,
        filterModel,
        setPaginationModel,
        setSortModel,
        setFilterModel,
        apiQueryParams,
        searchParams,
        updateParams,
    }
}
